/**
 * Global Search & Smart Navigation (MVP)
 * -----------------------------------------------------------------------------
 * A lightweight, frontend-only universal search for HealthLoop. Users can
 * search for features by intent ("steps", "pharmacy", "pregnancy") and jump
 * directly to the relevant page — no more guessing where a tracker lives.
 *
 * Design choices, deliberately:
 *   - Pure client-side indexing (FEATURE_INDEX). Zero backend, zero network.
 *   - Simple scored matching (title > keywords > description). Good enough
 *     for ~40 entries; cheap on mobile; predictable behaviour for users.
 *   - Mint-green HealthLoop palette throughout — no purple gradient slop.
 *   - Mobile-first: full-height sheet on small screens, centred dialog on
 *     desktop. Input autofocuses, keyboard never blocks results because the
 *     results list is independently scrollable with safe-area padding.
 *   - Recent searches in localStorage (capped to 5). Optional, lightweight.
 *   - Coming-Soon features remain searchable but render a clear badge so we
 *     never mislead users about availability.
 *   - Trigger surface is intentionally minimal — a search bar on the Home
 *     screen and a search icon in the header. Cmd/Ctrl+K opens it from
 *     anywhere for keyboard users without adding visual weight.
 *
 * Scalability hooks (not implemented now, just structured for it):
 *   - `scoreEntry` is the only ranking function — swap it for fuzzy/AI later.
 *   - FEATURE_INDEX is a flat array — easy to feed into a vector store later.
 *   - Recent searches schema lives under one localStorage key — future
 *     personalisation/recommendation passes can read it as a signal.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Clock, ArrowRight, Sparkles, Compass } from 'lucide-react';

import { FEATURE_INDEX, POPULAR_SUGGESTIONS } from '@/data/featureIndex';
import { useLanguage } from '@/contexts/LanguageContext';

const RECENT_SEARCHES_KEY = 'healthloop.globalSearch.recent';
const RECENT_LIMIT = 5;

// Section order matches BottomNav so grouped results feel familiar.
const SECTION_ORDER = ['Home', 'Nutrition', 'Family', 'Services', 'Support', 'Profile'];
const SECTION_KEY_MAP = {
  Home: 'home',
  Nutrition: 'nutrition',
  Family: 'family',
  Services: 'services',
  Support: 'support',
  Profile: 'profile',
};

/**
 * Lightweight scoring. Higher = better match.
 *  - Exact title match: 100
 *  - Title prefix:       60
 *  - Title contains:     40
 *  - Keyword exact:      35
 *  - Keyword contains:   20
 *  - Description hit:    10
 * Multiple-word queries: each word scored independently; sum is the total.
 */
function scoreEntry(entry, normalizedQuery) {
  if (!normalizedQuery) return 0;
  const words = normalizedQuery.split(/\s+/).filter(Boolean);
  if (words.length === 0) return 0;

  const title = entry.title.toLowerCase();
  const desc = (entry.description || '').toLowerCase();
  const keywords = (entry.keywords || []).map((k) => k.toLowerCase());

  let total = 0;
  for (const w of words) {
    let best = 0;
    if (title === w) best = Math.max(best, 100);
    else if (title.startsWith(w)) best = Math.max(best, 60);
    else if (title.includes(w)) best = Math.max(best, 40);

    for (const k of keywords) {
      if (k === w) best = Math.max(best, 35);
      else if (k.includes(w)) best = Math.max(best, 20);
    }
    if (desc.includes(w)) best = Math.max(best, 10);
    total += best;
  }
  return total;
}

function loadRecent() {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === 'string').slice(0, RECENT_LIMIT) : [];
  } catch (_) {
    return [];
  }
}

function saveRecent(query) {
  if (!query || !query.trim()) return;
  try {
    const current = loadRecent();
    const trimmed = query.trim();
    const next = [trimmed, ...current.filter((q) => q.toLowerCase() !== trimmed.toLowerCase())].slice(0, RECENT_LIMIT);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
  } catch (_) {
    // localStorage failures are non-fatal — search still works in-session.
  }
}

function clearRecent() {
  try { localStorage.removeItem(RECENT_SEARCHES_KEY); } catch (_) {}
}

/**
 * GlobalSearch — controlled modal. Parent owns `open` so the same component
 * can be triggered from multiple places (header icon, hero search bar,
 * keyboard shortcut) without duplicating state.
 */
export function GlobalSearch({ open, onClose }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState([]);

  // Refresh recent list every time the modal opens so it stays in sync with
  // anything saved during the previous open/navigate cycle.
  useEffect(() => {
    if (open) {
      setRecent(loadRecent());
      setQuery('');
      // Autofocus after the modal mounts — slight delay so mobile keyboards
      // open reliably instead of being suppressed by the transition.
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Esc closes; lock body scroll while open so background doesn't bleed.
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const results = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return [];
    const scored = FEATURE_INDEX
      .map((entry) => ({ entry, score: scoreEntry(entry, normalized) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score);
    // Group by section for visual clarity while preserving rank within group.
    const grouped = {};
    for (const { entry } of scored) {
      if (!grouped[entry.section]) grouped[entry.section] = [];
      grouped[entry.section].push(entry);
    }
    return SECTION_ORDER
      .filter((s) => grouped[s])
      .map((section) => ({ section, items: grouped[section] }));
  }, [query]);

  const totalResults = results.reduce((sum, g) => sum + g.items.length, 0);

  const handleSelect = useCallback((entry) => {
    saveRecent(query || entry.title);
    onClose();
    // Small defer so the modal close animation finishes before route change.
    setTimeout(() => navigate(entry.route), 50);
  }, [query, onClose, navigate]);

  const handleSuggestionClick = useCallback((term) => {
    setQuery(term);
    inputRef.current?.focus();
  }, []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-slate-900/40 backdrop-blur-sm px-0 sm:px-4 sm:pt-16"
      onClick={onClose}
      data-testid="global-search-overlay"
      role="presentation"
    >
      <div
        className="w-full sm:max-w-2xl bg-white sm:rounded-2xl shadow-2xl border border-emerald-100 flex flex-col h-full sm:h-auto sm:max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('search.modalAriaLabel')}
        data-testid="global-search-modal"
      >
        {/* Input row — sticky on mobile so keyboard never hides it */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-emerald-100 bg-gradient-to-r from-emerald-50 to-teal-50">
          <Search className="h-5 w-5 text-emerald-600 shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            inputMode="search"
            autoComplete="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('search.placeholder')}
            className="flex-1 bg-transparent outline-none text-base text-slate-800 placeholder:text-slate-400"
            data-testid="global-search-input"
            aria-label={t('search.inputAriaLabel')}
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(''); inputRef.current?.focus(); }}
              className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-full hover:bg-white/60"
              aria-label={t('search.clearAriaLabel')}
              data-testid="global-search-clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="ml-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 px-2 py-1 rounded-md hover:bg-white/60 transition-colors"
            data-testid="global-search-close"
          >
            {t('search.close')}
          </button>
        </div>

        {/* Scrollable results / empty state */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]"
          data-testid="global-search-results-scroll"
        >
          {/* Empty-state (no query yet) */}
          {!query && (
            <div className="px-4 py-5 space-y-6" data-testid="global-search-empty-state">
              {recent.length > 0 && (
                <section>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <Clock className="h-3.5 w-3.5" /> {t('search.recentSearches')}
                    </div>
                    <button
                      type="button"
                      onClick={() => { clearRecent(); setRecent([]); }}
                      className="text-xs text-slate-400 hover:text-slate-600"
                      data-testid="global-search-clear-recent"
                    >
                      {t('search.clear')}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {recent.map((term) => (
                      <button
                        key={term}
                        type="button"
                        onClick={() => handleSuggestionClick(term)}
                        className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-100 hover:bg-emerald-100 transition-colors"
                        data-testid={`global-search-recent-${term.replace(/\s+/g, '-')}`}
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              <section>
                <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <Sparkles className="h-3.5 w-3.5" /> {t('search.tryFor')}
                </div>
                <div className="flex flex-wrap gap-2">
                  {POPULAR_SUGGESTIONS.map((term) => (
                    <button
                      key={term}
                      type="button"
                      onClick={() => handleSuggestionClick(term)}
                      className="px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-slate-700 text-sm hover:bg-emerald-50 hover:border-emerald-300 transition-colors"
                      data-testid={`global-search-suggestion-${term.replace(/\s+/g, '-')}`}
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </section>

              <section className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4 flex items-start gap-3">
                <Compass className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-slate-800 mb-1">{t('search.findFeatures')}</p>
                  <p>
                    {t('search.searchAcross')}{' '}
                    <span className="font-medium text-slate-700">{t('search.sections.home')}</span>,{' '}
                    <span className="font-medium text-slate-700">{t('search.sections.nutrition')}</span>,{' '}
                    <span className="font-medium text-slate-700">{t('search.sections.family')}</span>,{' '}
                    <span className="font-medium text-slate-700">{t('search.sections.services')}</span>,{' '}
                    <span className="font-medium text-slate-700">{t('search.sections.support')}</span> {t('search.and')}{' '}
                    <span className="font-medium text-slate-700">{t('search.sections.profile')}</span>.
                  </p>
                </div>
              </section>
            </div>
          )}

          {/* No results */}
          {query && totalResults === 0 && (
            <div
              className="px-6 py-12 text-center"
              data-testid="global-search-no-results"
            >
              <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
                <Search className="h-6 w-6 text-emerald-400" />
              </div>
              <p className="text-base font-semibold text-slate-800">
                {t('search.noMatchesFor', { query })}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {t('search.trySimpler')}
              </p>
            </div>
          )}

          {/* Grouped results */}
          {query && totalResults > 0 && (
            <div className="py-2" data-testid="global-search-results">
              {results.map(({ section, items }) => (
                <section key={section} className="px-2 pb-2">
                  <div className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700/80">
                    {t(`search.sections.${SECTION_KEY_MAP[section] || section.toLowerCase()}`, section)}
                  </div>
                  <ul className="space-y-1">
                    {items.map((entry) => {
                      const Icon = entry.icon || Search;
                      const isComingSoon = entry.status === 'coming-soon';
                      return (
                        <li key={entry.id}>
                          <button
                            type="button"
                            onClick={() => handleSelect(entry)}
                            className="w-full text-left flex items-start gap-3 px-3 py-3 rounded-xl hover:bg-emerald-50 active:bg-emerald-100 transition-colors group"
                            data-testid={`global-search-result-${entry.id}`}
                          >
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 group-hover:bg-emerald-200 transition-colors">
                              <Icon className="h-5 w-5" aria-hidden="true" />
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-slate-800 truncate">
                                  {entry.title}
                                </span>
                                {isComingSoon && (
                                  <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                    {t('search.comingSoonBadge')}
                                  </span>
                                )}
                              </span>
                              <span className="block text-sm text-slate-500 line-clamp-2">
                                {entry.description}
                              </span>
                            </span>
                            <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-600 mt-3 shrink-0 transition-colors" aria-hidden="true" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="hidden sm:flex items-center justify-between px-4 py-2 border-t border-emerald-100 bg-emerald-50/40 text-[11px] text-slate-500">
          <span>
            <kbd className="px-1.5 py-0.5 rounded border border-slate-300 bg-white font-sans text-[10px] text-slate-600">Esc</kbd>{' '}
            {t('search.escToClose')}
          </span>
          <span>{totalResults > 0
            ? t(totalResults === 1 ? 'search.resultSingular' : 'search.resultPlural', { count: totalResults })
            : t('search.searchAcrossApp')}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * GlobalSearchTrigger — small button (icon or pill) that opens the modal.
 * Variants:
 *   - `icon`    : compact magnifier (header)
 *   - `pill`    : full-width search-bar style with placeholder (home hero)
 */
export function GlobalSearchTrigger({ variant = 'icon', onClick, className = '' }) {
  const { t } = useLanguage();
  if (variant === 'pill') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-emerald-100 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all text-left group ${className}`}
        data-testid="global-search-trigger-pill"
        aria-label={t('search.triggerAriaLabel')}
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100 transition-colors">
          <Search className="h-4.5 w-4.5" aria-hidden="true" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium text-slate-800">{t('search.triggerLabel')}</span>
          <span className="block text-xs text-slate-500 truncate">
            {t('search.triggerHint')}
          </span>
        </span>
        <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-[10px] font-sans text-slate-500">
          ⌘K
        </kbd>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative p-2 rounded-full text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors ${className}`}
      data-testid="global-search-trigger-icon"
      aria-label={t('search.triggerAriaLabel')}
      title={t('search.triggerLabel')}
    >
      <Search className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}

/**
 * useGlobalSearch — convenience hook: wires Cmd/Ctrl+K and exposes open/close.
 * Use this in the page/component that owns the trigger so the same shortcut
 * works regardless of which surface the user is on.
 */
export function useGlobalSearch() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      const isMac = navigator.platform?.toUpperCase().includes('MAC');
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      if (cmdOrCtrl && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  return {
    open,
    openSearch: () => setOpen(true),
    closeSearch: () => setOpen(false),
  };
}

export default GlobalSearch;
